import React, { useState } from 'react';
import {
  Avatar, Box, Button, Chip, Divider, Grid, List, ListItem, ListItemAvatar,
  ListItemText, Tooltip, Typography,
} from '@mui/material';
import { Block, CancelOutlined, CheckCircleOutline, LockOpen } from '@mui/icons-material';
import { BaseDialog } from '../common/BaseDialog';
import { ConfirmationDialog } from '../common/ConfirmationDialog';
import { AdminClientDetail, ConnectionStatus, adminUsersService } from '../../services/admin-users.service';
import { ImpersonateButton } from './ImpersonateButton';
import { AdminClientDailyNormsTab } from './AdminClientDailyNormsTab';
import { AdaptiveTabs, AdaptiveTabItem } from '../common/AdaptiveTabs';
import { UserStatusChip } from './UserStatusChip';
import { toastService } from '../../services/toast.service';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

type ClientDetailTab = 'profile' | 'experts' | 'daily-norms';

interface Props {
  client: AdminClientDetail | null;
  open: boolean;
  onClose: () => void;
  onStatusChanged?: (userId: number, status: number) => void;
}

const GENDER_LABEL: Record<string, string> = {
  male: 'Чоловіча',
  female: 'Жіноча',
};

const CONNECTION_STATUS: Record<ConnectionStatus, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  active:     { label: 'Активний',  color: 'success' },
  pending:    { label: 'Очікує',    color: 'warning' },
  rejected:   { label: 'Відхилено', color: 'error'   },
  terminated: { label: 'Завершено', color: 'default' },
};

const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body1">{value ?? '—'}</Typography>
  </Grid>
);

export const AdminClientDetailModal: React.FC<Props> = ({ client, open, onClose, onStatusChanged }) => {
  const [tab, setTab] = useState<ClientDetailTab>('profile');
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);

  const handleClose = () => { setTab('profile'); onClose(); };

  const handleConfirmStatusToggle = async () => {
    if (!client) return;
    const newStatus = client.status === 1 ? 2 : 1;
    try {
      await adminUsersService.setUserStatus('clients', client.id, newStatus);
      onStatusChanged?.(client.id, newStatus);
      toastService.success(newStatus === 2 ? 'Клієнта заблоковано.' : 'Клієнта розблоковано.');
    } catch {
      toastService.error('Не вдалося змінити статус клієнта.');
    } finally {
      setStatusConfirmOpen(false);
    }
  };

  if (!client) return null;

  const fullName = [client.name, client.surname].filter(Boolean).join(' ');
  const expertsCount = client.connectedExperts.length;
  const tabs: AdaptiveTabItem[] = [
    { value: 'profile', label: 'Профіль' },
    { value: 'experts', label: expertsCount > 0 ? `Експерти (${expertsCount})` : 'Експерти' },
    { value: 'daily-norms', label: 'Розрахунок денних норм' },
  ];

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar src={client.avatar} sx={{ width: 40, height: 40 }}>
        {client.name.charAt(0)}
      </Avatar>
      <Box>
        <Typography variant="h6" lineHeight={1.2}>{fullName}</Typography>
        <Typography variant="body2" color="text.secondary">{client.email}</Typography>
      </Box>
    </Box>
  );

  return (
    <>
    <BaseDialog
      open={open}
      onClose={handleClose}
      title={title}
      maxWidth="md"
      actions={
        <>
          {client.status !== 3 && (
            <Button
              color={client.status === 1 ? 'error' : 'success'}
              startIcon={client.status === 1 ? <Block /> : <LockOpen />}
              onClick={() => setStatusConfirmOpen(true)}
            >
              {client.status === 1 ? 'Заблокувати' : 'Розблокувати'}
            </Button>
          )}
          <ImpersonateButton userId={client.id} userName={fullName} onSuccess={handleClose} />
        </>
      }
      data-i-doc={ModalIndices.ADMIN_CLIENT_DETAIL}
    >
      {/* Модалка на сторінці списку - керований режим (без ?tab= в URL списку), при закритті - на "Профіль". */}
      <Box sx={{ mb: 2, mt: -1 }}>
        <AdaptiveTabs tabs={tabs} value={tab} onChange={(v) => setTab(v as ClientDetailTab)} />
      </Box>

      {tab === 'daily-norms' && <AdminClientDailyNormsTab clientId={client.id} />}

      {tab === 'profile' && (
        <>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Основна інформація
          </Typography>
          <Grid container spacing={2}>
            <Row label="ID" value={client.id} />
            <Row label="Ім'я" value={client.name} />
            <Row label="Прізвище" value={client.surname} />
            <Row label="Телефон" value={client.phone} />
            <Row label="Стать" value={client.gender ? GENDER_LABEL[client.gender] : undefined} />
            <Row label="Рік народження" value={client.bornYear} />
            <Row label="Часовий пояс" value={client.timezone} />
            <Row label="Мова" value={client.locale?.toUpperCase()} />
            <Row label="Код клієнта" value={client.clientCode} />
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <Typography variant="body1">{client.email}</Typography>
                {client.isEmailVerified ? (
                  <Tooltip title="Верифіковано"><CheckCircleOutline color="success" sx={{ fontSize: 16 }} /></Tooltip>
                ) : (
                  <Tooltip title="Не верифіковано"><CancelOutlined color="disabled" sx={{ fontSize: 16 }} /></Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Статус акаунту</Typography>
              <Box sx={{ mt: 0.5 }}>
                <UserStatusChip status={client.status} isProtectedDemoAccount={client.isProtectedDemoAccount} />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Активність
          </Typography>
          <Grid container spacing={2}>
            <Row
              label="Останній вхід"
              value={client.lastLoginAt ? new Date(client.lastLoginAt).toLocaleString('uk-UA') : undefined}
            />
            <Row
              label="Дата реєстрації"
              value={new Date(client.createdAt).toLocaleString('uk-UA')}
            />
          </Grid>
        </>
      )}

      {tab === 'experts' && (
        expertsCount === 0 ? (
          <Typography variant="body2" color="text.secondary">Немає підключених експертів</Typography>
        ) : (
          <List dense disablePadding>
            {client.connectedExperts.map((e) => {
              const statusCfg = CONNECTION_STATUS[e.connectionStatus];
              return (
                <ListItem key={e.id} disableGutters>
                  <ListItemAvatar>
                    <Avatar src={e.avatar} sx={{ width: 32, height: 32 }}>
                      {e.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={`${e.name} ${e.surname}`} />
                  <Chip label={statusCfg.label} color={statusCfg.color} size="small" variant="outlined" />
                </ListItem>
              );
            })}
          </List>
        )
      )}
    </BaseDialog>

    <ConfirmationDialog
      open={statusConfirmOpen}
      onClose={() => setStatusConfirmOpen(false)}
      onConfirm={handleConfirmStatusToggle}
      title={client.status === 1 ? 'Заблокувати клієнта?' : 'Розблокувати клієнта?'}
      message={
        client.status === 1
          ? `${fullName} втратить доступ до застосунку. Дані не видаляються — дію можна скасувати.`
          : `${fullName} знову зможе увійти в застосунок.`
      }
      confirmText={client.status === 1 ? 'Заблокувати' : 'Розблокувати'}
    />
    </>
  );
};
